import React, { useState } from "react";
import * as yup from "yup";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, MenuItem, Typography
} from "@mui/material";
import { Field, FieldArray, Form, Formik, FormikProps } from "formik";
import { TextInput } from "../../../../components/inputs";
import { SetState } from "../../../../app/types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextField } from "@mui/material";

export interface JobFilter {
  column: string;
  operator: string;
  value: string;
}

type FilterInput = {
  id?: string;
  documentId?: string;
  externalId?: string;
  synchronised?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  tenantId?: string;
  internalId?: string;
  externalStatus?: string;
  tenant?: string;
  customer?: string;
  createdDate?: string;
  updatedTime?: string;
}

const getFilterableColumns = (jobType: string | undefined): { key: keyof FilterInput, label: string }[] => {
  switch (jobType) {
    case "documents":
      return [
        { key: "id", label: "ID" },
        { key: "documentId", label: "Document ID" },
        { key: "externalId", label: "External ID" },
        { key: "type", label: "Type" },
        { key: "createdAt", label: "Created" },
        { key: "updatedAt", label: "Updated" },
        { key: "status", label: "Status" },
      ];
    case "bulk-valuation":
      return [
        { key: "tenantId", label: "Tenant ID" },
        { key: "id", label: "ID" },
        { key: "externalId", label: "External ID" },
        { key: "internalId", label: "Internal ID" },
        { key: "createdAt", label: "Created" },
        { key: "externalStatus", label: "External Status" },
        { key: "status", label: "Status" },
      ];
    case "data-sync":
      return [
        { key: "tenant", label: "Tenant" },
        { key: "id", label: "ID" },
        { key: "customer", label: "Customer" },
        { key: "createdDate", label: "Created Date" },
        { key: "updatedTime", label: "Updated Time" },
        { key: "status", label: "Status" },
      ];
    default:
      return [];
  }
};

const operations = [
  { key: "eq", label: "Equal to" },
  { key: "between", label: "Between" },
  { key: "contains", label: "Contains" }
];

const dateColumns = ["createdAt", "updatedAt", "createdDate", "updatedTime"];

const isDateColumn = (columnName: string): boolean => {
  return dateColumns.includes(columnName);
};

const validationSchema = yup.object({
  filters: yup.array(yup.object({
    name: yup.string().required().label("Name")
      .test("unique-column", "This column is already used in another filter", function(value) {
        if (!value) return true;
        const { path, parent, from } = this;
        const filters = from?.[1]?.value?.filters || [];
        const currentIndex = parseInt(path.split("[")[1]?.split("]")[0] || "0");
        const duplicateCount = filters.filter((f: InputValues, idx: number) =>
          f.name === value && idx !== currentIndex
        ).length;
        return duplicateCount === 0;
      }),
    operation: yup.string().required().label("Operation")
      .test("date-column-operation", "Date columns only support 'Between' operation", function(value) {
        const { parent } = this;
        const columnName = parent.name;
        if (isDateColumn(columnName) && value !== "between") {
          return false;
        }
        return true;
      }),
    value: yup
      .mixed()
      .when(["operation", "name"], ([operation, name], schema) => {
        if (operation === "between") return schema.optional();
        if (isDateColumn(name as string)) return yup.date().required().label("Value");
        return yup.string().required().label("Value");
      }),
    from: yup
      .mixed()
      .when(["operation", "name"], ([operation, name], schema) => {
        if (operation !== "between") return schema.optional();
        if (isDateColumn(name as string)) return yup.date().required().label("From");
        return yup.string().required().label("From");
      }),
    to: yup
      .mixed()
      .when(["operation", "name"], ([operation, name], schema) => {
        if (operation !== "between") return schema.optional();
        if (isDateColumn(name as string)) return yup.date().required().label("To");
        return yup.string().required().label("To");
      }),
  }))
});

export type InputValues = {
  name: keyof FilterInput | "",
  operation: string,
  value: string | Date | null,
  from: string | Date | null,
  to: string | Date | null
};

type InitialValues = {
  filters: InputValues[]
}

const initialValues: InitialValues = {
  filters: [{ name: "", operation: "", value: "", from: "", to: "" }]
};

interface CustomJobFilterProps {
  jobType: string | undefined;
  onFiltersChange: (filters: JobFilter[]) => void;
}

const transformStatusValue = (value: string, jobType: string | undefined): string => {
  if (jobType === "bulk-valuation" && value.toLowerCase() === "failed") {
    return "Error";
  }
  if (jobType === "data-sync") {
    return value.toLowerCase();
  }
  return value;
};

const JobFilter: React.FC<CustomJobFilterProps> = ({ jobType, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const fields = getFilterableColumns(jobType);

  const onSubmit = (values: InitialValues) => {
    const jobFilters: JobFilter[] = values.filters.map((filter) => {
      const isDate = isDateColumn(filter.name as string);
      const isStatusColumn = filter.name === "status";

      let filterValue: string;
      if (filter.operation === "between") {
        let fromValue: string;
        let toValue: string;

        if (isDate && filter.from instanceof Date) {
          const fromDate = new Date(filter.from);
          fromDate.setHours(0, 0, 0, 0);
          fromValue = fromDate.toISOString();
        } else {
          fromValue = filter.from as string;
        }

        if (isDate && filter.to instanceof Date) {
          const toDate = new Date(filter.to);
          toDate.setHours(23, 59, 59, 999);
          toValue = toDate.toISOString();
        } else {
          toValue = filter.to as string;
        }

        filterValue = `${fromValue}|${toValue}`;
      } else {
        filterValue = isDate && filter.value instanceof Date
          ? filter.value.toISOString()
          : String(filter.value);
      }

      if (isStatusColumn) {
        filterValue = transformStatusValue(filterValue, jobType);
      }

      return {
        column: filter.name as string,
        operator: filter.operation,
        value: filterValue,
      };
    });
    onFiltersChange(jobFilters);
  };

  return (
    <Box sx={{ marginBottom: "20px" }}>
      <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <AccordionSummary sx={{ "&.Mui-expanded": { minHeight: "32px" } }} expandIcon={<ExpandMoreIcon />}>
          <Typography>Filters</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "16px" }}>
          <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
                {(props: FormikProps<InitialValues>) => {
                  const { values, setFieldValue } = props;
                  React.useEffect(() => {
                    values.filters.forEach((filter, index) => {
                      if (isDateColumn(filter.name as string) && filter.operation !== "between") {
                        setFieldValue(`filters[${index}].operation`, "between");
                      }
                    });
                  }, [values.filters.map(f => f.name).join(",")]);

                  return (
                    <Form>
                      <FieldArray
                        name="filters"
                        render={arrayHelpers => (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 80px", gap: "20px" }}>
                              {values.filters.map((_, index) => {
                                const currentColumn = values.filters[index].name;
                                const isDate = isDateColumn(currentColumn as string);
                                const availableOperations = isDate
                                  ? operations.filter(op => op.key === "between")
                                  : operations;

                                return (
                                  <>
                                    <Field
                                      select
                                      size="small"
                                      label="Name"
                                      component={TextInput}
                                      name={`filters[${index}].name`}
                                    >
                                      {fields.map((el) => (
                                        <MenuItem key={el.key} value={el.key}>{el.label}</MenuItem>
                                      ))}
                                    </Field>
                                    <Field
                                      select
                                      size="small"
                                      label="Operation"
                                      component={TextInput}
                                      name={`filters[${index}].operation`}
                                    >
                                      {availableOperations.map((el) => (
                                        <MenuItem key={el.key} value={el.key}>{el.label}</MenuItem>
                                      ))}
                                    </Field>
                                    {values.filters[index].operation === "between"
                                      ? (
                                        <Box sx={{ display: "flex", gap: "20px" }}>
                                          {isDate ? (
                                            <>
                                              <DatePicker
                                                value={(values.filters[index].from instanceof Date ? values.filters[index].from : null) as Date | null}
                                                onChange={(newValue) => setFieldValue(`filters[${index}].from`, newValue)}
                                                format="MM/dd/yyyy"
                                                slotProps={{
                                                  textField: {
                                                    size: "small",
                                                    label: "From",
                                                    fullWidth: true,
                                                  }
                                                }}
                                              />
                                              <DatePicker
                                                value={(values.filters[index].to instanceof Date ? values.filters[index].to : null) as Date | null}
                                                onChange={(newValue) => setFieldValue(`filters[${index}].to`, newValue)}
                                                format="MM/dd/yyyy"
                                                slotProps={{
                                                  textField: {
                                                    size: "small",
                                                    label: "To",
                                                    fullWidth: true,
                                                  }
                                                }}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <Field
                                                size="small"
                                                label="From"
                                                component={TextInput}
                                                name={`filters[${index}].from`}
                                              />
                                              <Field
                                                size="small"
                                                label="To"
                                                component={TextInput}
                                                name={`filters[${index}].to`}
                                              />
                                            </>
                                          )}
                                        </Box>
                                      ) : (
                                        isDate ? (
                                          <DatePicker
                                            value={(values.filters[index].value instanceof Date ? values.filters[index].value : null) as Date | null}
                                            onChange={(newValue) => setFieldValue(`filters[${index}].value`, newValue)}
                                            format="MM/dd/yyyy"
                                            slotProps={{
                                              textField: {
                                                size: "small",
                                                label: "Value",
                                                fullWidth: true,
                                              }
                                            }}
                                          />
                                        ) : (
                                          <Field
                                            size="small"
                                            label="Value"
                                            component={TextInput}
                                            name={`filters[${index}].value`}
                                          />
                                        )
                                      )
                                    }
                                    <Button
                                      type="button"
                                      size="small"
                                      variant="outlined"
                                      sx={{ maxHeight: "38px" }}
                                      color="primary"
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                    Remove
                                    </Button>
                                  </>
                                );
                              })}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "20px" }}>
                              <Button
                                variant="outlined"
                                type="button"
                                onClick={() => arrayHelpers.push(initialValues.filters[0])}
                              >
                              Add a filter
                              </Button>
                              <Button variant="contained" type="submit">Submit</Button>
                            </Box>
                          </Box>
                        )}
                      />
                    </Form>
                  );
                }}
              </Formik>
            </LocalizationProvider>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default JobFilter;
